import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, message, Modal, Form, Input, Select, Radio, InputNumber, Switch, Upload, Space, Typography, Alert, DatePicker, Checkbox, Divider, ColorPicker } from 'antd';
import { PlusOutlined, UploadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { memberCardApi } from '../../services/api';

const { TextArea } = Input;
const { Title } = Typography;

// 品牌API会员卡状态
const STATE_MAP = {
  CARD_EFFECTIVE: { color: 'green', text: '生效中' },
  CARD_INVALID: { color: 'default', text: '已失效' },
};

const CODE_TYPE_MAP = {
  NONE_CODE: '不显示码',
  BAR_CODE: '条形码',
  QR_CODE: '二维码',
  BAR_CODE_AND_QR_CODE: '条形码+二维码',
  JUMP_MINI_PROGRAM: '跳转小程序',
};

const DATE_TYPE_MAP = {
  PERMANENT: '永久有效',
  FIX_TERM: '领取后N天有效',
  FIX_TIME_RANGE: '固定时间段',
};

const COMMON_FIELD_OPTIONS = [
  { value: 'USER_FORM_FLAG_SEX', label: '性别' },
  { value: 'USER_FORM_FLAG_NAME', label: '姓名' },
  { value: 'USER_FORM_FLAG_BIRTHDAY', label: '生日' },
  { value: 'USER_FORM_FLAG_ADDRESS', label: '地址' },
  { value: 'USER_FORM_FLAG_EMAIL', label: '邮箱' },
  { value: 'USER_FORM_FLAG_CITY', label: '城市' },
];

// 卡图片上传（经后端中转到品牌API图片上传接口）
const CardImageUploader = ({ value = {}, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const customRequest = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await memberCardApi.uploadImage(formData);
      onChange({ url: res.data.url });
      message.success('卡图片上传成功');
      onSuccess(res);
    } catch (err) {
      message.error(err.message || '卡图片上传失败');
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file) => {
    const isImg = ['image/jpeg', 'image/png', 'image/bmp'].includes(file.type);
    if (!isImg) {
      message.error('仅支持 JPG/PNG/BMP 格式');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 1024 * 1024) {
      message.error('图片不能超过 1MB');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  return (
    <div>
      <Upload
        accept="image/jpeg,image/png,image/bmp"
        listType="picture-card"
        showUploadList={false}
        customRequest={customRequest}
        beforeUpload={beforeUpload}
      >
        {value?.url ? (
          <img src={value.url} alt="卡图片" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <div>
            <UploadOutlined />
            <div style={{ marginTop: 8 }}>{uploading ? '上传中...' : '上传卡图片'}</div>
          </div>
        )}
      </Upload>
      <div style={{ color: '#999', fontSize: 12 }}>建议尺寸 716×320 像素，JPG/PNG/BMP，小于1M；将上传到微信图片服务</div>
    </div>
  );
};

const MemberCards = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const dateType = Form.useWatch('dateType', form);
  const codeType = Form.useWatch('codeType', form);
  const needDisplayLevel = Form.useWatch('needDisplayLevel', form);
  const needPinned = Form.useWatch('needPinned', form);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await memberCardApi.list();
      setData(res.data || []);
    } catch (err) {
      message.error(err.message || '加载会员卡列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // 表单校验错误
    }
    setCreating(true);
    try {
      const payload = {
        title: values.title,
        cardColor: values.cardColor,
        cardPictureUrl: values.cardPicture?.url,
        codeType: values.codeType,
        codeMode: values.codeMode,
        benefits: values.benefits,
        notifyUrl: values.notifyUrl,
        legalAgreement: values.legalAgreement,
        servicePhone: values.servicePhone || undefined,
        dateType: values.dateType,
        memberJumpAppid: values.memberJumpAppid,
        memberJumpPath: values.memberJumpPath,
      };

      if (values.dateType === 'FIX_TIME_RANGE') {
        const [begin, end] = values.timeRange;
        payload.availableBeginTime = begin.format('YYYY-MM-DDTHH:mm:ss+08:00');
        payload.availableEndTime = end.format('YYYY-MM-DDTHH:mm:ss+08:00');
      }
      if (values.dateType === 'FIX_TERM') {
        payload.availableDayAfterReceive = values.availableDayAfterReceive ?? 0;
      }
      if (values.codeType === 'JUMP_MINI_PROGRAM') {
        payload.codeJumpAppid = values.codeJumpAppid;
        if (values.codeJumpPath) payload.codeJumpPath = values.codeJumpPath;
      }
      if (values.needPinned) payload.needPinned = true;
      if (values.needDisplayLevel) {
        payload.needDisplayLevel = true;
        payload.initLevel = values.initLevel;
      }
      if (values.pointsJumpAppid) {
        payload.pointsJumpAppid = values.pointsJumpAppid;
        if (values.pointsJumpPath) payload.pointsJumpPath = values.pointsJumpPath;
      }
      if (values.balanceJumpAppid) {
        payload.balanceJumpAppid = values.balanceJumpAppid;
        if (values.balanceJumpPath) payload.balanceJumpPath = values.balanceJumpPath;
      }
      if (values.commonFields?.length) payload.commonFields = values.commonFields;
      if (values.customFields?.length) {
        payload.customFields = values.customFields
          .filter(f => f && f.name)
          .map(f => ({ type: f.type, name: f.name, values: f.values }));
      }

      const res = await memberCardApi.create(payload);
      message.success(res.message || '创建成功', 6);
      Modal.success({
        title: '会员卡创建成功',
        content: (
          <div>
            <p>卡模板ID：<code>{res.data?.cardId}</code></p>
            <p>状态：{STATE_MAP[res.data?.state]?.text || res.data?.state}</p>
          </div>
        ),
      });
      setModalOpen(false);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error(err.message || '创建失败', 8);
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '卡模板ID', dataIndex: 'card_id', width: 220,
      render: (v) => v ? <code style={{ fontSize: 12 }}>{v}</code> : '-'
    },
    { title: '卡名称', dataIndex: 'title', width: 120 },
    {
      title: '卡图', dataIndex: 'card_picture_url', width: 80,
      render: (v) => v ? <img src={v} alt="卡图" style={{ width: 52, height: 26, objectFit: 'contain' }} /> : '-'
    },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v) => {
        const s = STATE_MAP[v] || { color: 'orange', text: v || '未知' };
        return <Tag color={s.color}>{s.text}</Tag>;
      }
    },
    { title: '码型', dataIndex: 'code_type', width: 110, render: (v) => CODE_TYPE_MAP[v] || v },
    { title: '有效期', dataIndex: 'date_type', width: 120, render: (v) => DATE_TYPE_MAP[v] || v },
    {
      title: '创建时间', dataIndex: 'created_at', width: 150,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm')
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>会员卡管理</Title>
        <Button type="primary" icon={<PlusOutlined />} style={{ background: '#8B7355' }} onClick={() => setModalOpen(true)}>
          创建会员卡
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
      />

      <Modal
        title="创建会员卡"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="提交创建"
        cancelText="取消"
        width={680}
        okButtonProps={{ style: { background: '#8B7355' } }}
      >
        <Alert type="info" showIcon style={{ marginBottom: 16 }}
          message="使用品牌API创建会员卡，需要「微信支付配置」中已填写品牌API凭证（品牌API证书、微信支付公钥ID）。"
        />
        <Form form={form} layout="vertical"
          initialValues={{ codeType: 'QR_CODE', codeMode: 'SYSTEM_ALLOCATE', dateType: 'PERMANENT', cardColor: '#8B7355', needDisplayLevel: false, needPinned: false }}>

          <Form.Item name="title" label="卡名称" rules={[{ required: true, message: '请输入卡名称' }, { max: 10, message: '最长10个中文字' }]}>
            <Input placeholder="展示在卡面上，如 得玉呈祥会员卡" maxLength={10} showCount />
          </Form.Item>

          <Form.Item
            name="cardColor" label="卡背景颜色" rules={[{ required: true, message: '请选择卡背景颜色' }]}
            getValueFromEvent={(color) => color.toHexString()}
          >
            <ColorPicker showText format="hex" />
          </Form.Item>

          <Form.Item name="cardPicture" label="卡图片" rules={[{ required: true, message: '请上传卡图片' }]}>
            <CardImageUploader />
          </Form.Item>

          <Form.Item name="benefits" label="会员权益" rules={[{ required: true, message: '请输入会员权益' }, { max: 32, message: '最长32个字符' }]}
            extra="为会员提供的专属优惠、服务或特权，如：全场9折、生日礼包">
            <Input placeholder="如 全场9折、生日礼包" maxLength={32} showCount />
          </Form.Item>

          <Form.Item name="notifyUrl" label="开卡成功回调地址" rules={[{ required: true, message: '请输入回调地址' }, { pattern: /^https:\/\//, message: '须为 https:// 开头' }]}
            extra="微信会在用户开卡成功后向该地址推送通知">
            <Input placeholder="https://getnephrite.com/api/dy/member-cards/notify" />
          </Form.Item>

          <Form.Item name="legalAgreement" label="商家法务协议" rules={[{ required: true, message: '请输入商家法务协议' }]}
            extra="具有法律效力的协议文本，纯文本展示（不支持链接），将展示给开卡用户">
            <TextArea rows={5} placeholder="如：会员卡使用协议……" />
          </Form.Item>

          <Form.Item name="servicePhone" label="服务电话（可选）">
            <Input placeholder="展示在会员卡详情内，建议填固定电话" />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>会员码</Divider>

          <Form.Item name="codeType" label="会员码展示类型" rules={[{ required: true }]}>
            <Select options={Object.entries(CODE_TYPE_MAP).map(([value, label]) => ({ value, label }))} />
          </Form.Item>

          {codeType === 'JUMP_MINI_PROGRAM' && (
            <>
              <Form.Item name="codeJumpAppid" label="会员码跳转小程序 AppID" rules={[{ required: true, message: '请输入小程序AppID' }]}>
                <Input placeholder="小程序 AppID" />
              </Form.Item>
              <Form.Item name="codeJumpPath" label="会员码跳转小程序 path（可选）">
                <Input placeholder="如 /pages/member/code" />
              </Form.Item>
            </>
          )}

          <Form.Item name="codeMode" label="会员code分配类型" rules={[{ required: true }]}
            extra="系统分配：领卡时微信分配24位数字；分配类型指定后不可修改">
            <Select options={[
              { value: 'SYSTEM_ALLOCATE', label: '系统分配（推荐）' },
              { value: 'MERCHANT_ALLOCATE', label: '商家分配' },
            ]} />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>有效期</Divider>

          <Form.Item name="dateType" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="PERMANENT">永久有效</Radio.Button>
              <Radio.Button value="FIX_TERM">领取后N天</Radio.Button>
              <Radio.Button value="FIX_TIME_RANGE">固定时间段</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {dateType === 'FIX_TERM' && (
            <Form.Item name="availableDayAfterReceive" label="领取后N自然天内有效" rules={[{ required: true, message: '请填写天数' }]}>
              <InputNumber min={0} max={10957} style={{ width: '100%' }} placeholder="如365表示一年" />
            </Form.Item>
          )}

          {dateType === 'FIX_TIME_RANGE' && (
            <Form.Item name="timeRange" label="有效时间段" rules={[{ required: true, message: '请选择起止时间' }]}>
              <DatePicker.RangePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Divider orientation="left" orientationMargin={0}>会员中心与跳转</Divider>

          <Form.Item name="memberJumpAppid" label="会员中心小程序 AppID" rules={[{ required: true, message: '请输入小程序AppID' }]}
            extra="用户点击卡面后跳转到你的小程序（必填）">
            <Input placeholder="小程序 AppID，如 wx1234567890abcdef" />
          </Form.Item>
          <Form.Item name="memberJumpPath" label="会员中心小程序页面 path" rules={[{ required: true, message: '请输入小程序页面path' }]}>
            <Input placeholder="如 /pages/member/index" />
          </Form.Item>

          <Form.Item name="pointsJumpAppid" label="积分跳转小程序 AppID（可选，填了启用积分展示）">
            <Input placeholder="不填则不启用" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.pointsJumpAppid !== cur.pointsJumpAppid}>
            {({ getFieldValue }) => getFieldValue('pointsJumpAppid') ? (
              <Form.Item name="pointsJumpPath" label="积分跳转小程序 path（可选）">
                <Input placeholder="如 /pages/points/index" />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item name="balanceJumpAppid" label="储值跳转小程序 AppID（可选，填了启用储值展示）">
            <Input placeholder="不填则不启用" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.balanceJumpAppid !== cur.balanceJumpAppid}>
            {({ getFieldValue }) => getFieldValue('balanceJumpAppid') ? (
              <Form.Item name="balanceJumpPath" label="储值跳转小程序 path（可选）">
                <Input placeholder="如 /pages/recharge/index" />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>展示选项</Divider>

          <Form.Item name="needPinned" label="置顶展示" valuePropName="checked" extra="同品牌允许多张置顶卡">
            <Switch />
          </Form.Item>

          <Form.Item name="needDisplayLevel" label="展示会员等级" valuePropName="checked">
            <Switch />
          </Form.Item>

          {needDisplayLevel && (
            <Form.Item name="initLevel" label="会员初始等级" rules={[{ required: true, message: '请输入初始等级' }, { max: 10, message: '最长10个字符' }]}>
              <Input placeholder="如 VIP1" maxLength={10} />
            </Form.Item>
          )}

          <Divider orientation="left" orientationMargin={0}>开卡信息（可选）</Divider>

          <Form.Item name="commonFields" label="用户开卡时需填写的通用字段">
            <Checkbox.Group options={COMMON_FIELD_OPTIONS} />
          </Form.Item>

          <Form.Item label="自定义开卡字段（最多1项）" style={{ marginBottom: 0 }}>
            <Form.List name="customFields">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item {...restField} name={[name, 'type']} rules={[{ required: true, message: '选择类型' }]}>
                        <Select options={[
                          { value: 'RADIO', label: '单选' },
                          { value: 'CHECK_BOX', label: '多选' },
                        ]} style={{ width: 100 }} placeholder="类型" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'name']} rules={[{ required: true, message: '填写名称' }, { max: 32, message: '最长32字' }]}>
                        <Input placeholder="信息项名称" style={{ width: 140 }} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'values']}
                        rules={[{ required: true, message: '填写可选值' }]}>
                        <Select mode="tags" style={{ width: 260 }} placeholder="输入可选值后回车，最多10个，单个≤8字"
                          tokenSeparators={[',', '，']} open={false} maxCount={10} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#999' }} />
                    </Space>
                  ))}
                  {fields.length === 0 && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add({ type: 'RADIO' })} icon={<PlusOutlined />} style={{ width: 200 }}>
                        添加自定义字段
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MemberCards;
